import type { FileUpload } from "./FileUpload";
import { newShortGuid } from "../util/types/guid";
import { parseDate } from "../util/date/parseDate";
import { formatOrDefault } from "../util/date/formatOrDefault";
import { action, computed, makeObservable, observable } from "mobx";
import { DicomTags } from "../app/viewer/network/contracts/dicom/DicomDataSet";

export interface IStudyInUpload {
    studyGuid: string;
    label: string;
    filesToUpload: FileUpload[];
    filesToUploadSize: number;
    studyInstanceUid: string;
    accessionNumber: string;
    studyDate: string;
    studyDateFormatted: string;
    studyTime: string;
    studyTimeFormatted: string;
    modality: string;
    studyDescription: string;
    seriesDescription: string;
}

export class StudyInUpload implements IStudyInUpload {
    public studyGuid: string;

    public filesToUpload: FileUpload[] = [];

    @observable
    public comment: string | null = null;

    constructor(filesToUpload: FileUpload[]) {
        makeObservable(this);

        this.filesToUpload = filesToUpload;
        this.studyGuid = newShortGuid();
    }

    @computed
    public get label(): string {
        return this.patientName + " - " + this.studyDescription + " (" + this.studyDateFormatted + " " + this.studyTimeFormatted + ")";
    }

    @action
    public setComment(comment: string | null) {
        this.comment = comment;
    }

    @computed
    public get dicomFiles(): FileUpload[] {
        return this.filesToUpload.filter(s => s.fileIsADicomFile);
    }

    @computed
    public get nonDicomFiles(): FileUpload[] {
        return this.filesToUpload.filter(s => !s.fileIsADicomFile);
    }

    public get filesToUploadSize(): number {
        let filesToUploadTotalSize = 0;

        this.filesToUpload.forEach(f => {
            filesToUploadTotalSize += f.size;
        });

        return filesToUploadTotalSize;
    }

    private get patientName(): string {
        if (!this.dicomFiles || this.dicomFiles.length === 0) return "";
        return this.dicomFiles[0].getDicomTagValue(DicomTags.PatientName) ?? "";
    }

    public get studyInstanceUid(): string {
        if (!this.dicomFiles || this.dicomFiles.length === 0) return "";
        return this.dicomFiles[0].getDicomTagValue(DicomTags.StudyInstanceUID);
    }

    public get accessionNumber(): string {
        if (!this.dicomFiles || this.dicomFiles.length === 0) return "";
        return this.dicomFiles[0].getDicomTagValue(DicomTags.AccessionNumber);
    }

    public get studyDate(): string {
        if (!this.dicomFiles || this.dicomFiles.length === 0) return "";
        return this.dicomFiles[0].getDicomTagValue(DicomTags.StudyDate);
    }

    public get studyDateFormatted(): string {
        const studyDate = this.studyDate;
        const date = parseDate(studyDate, "yyyyMMdd");
        return formatOrDefault(date, "dd/MM/yyyy", studyDate);
    }

    public get studyTime(): string {
        if (!this.dicomFiles || this.dicomFiles.length === 0) return "";
        return this.dicomFiles[0].getDicomTagValue(DicomTags.StudyTime);
    }

    public get studyTimeFormatted(): string {
        const studyTime = this.studyTime;
        const date = parseDate(studyTime, "HHmmss");
        return formatOrDefault(date, "HH:mm", studyTime);
    }

    public get modality(): string {
        if (!this.dicomFiles || this.dicomFiles.length === 0) return "";
        return this.dicomFiles[0].getDicomTagValue(DicomTags.Modality);
    }

    public get studyDescription(): string {
        if (!this.dicomFiles || this.dicomFiles.length === 0) return "";
        return this.dicomFiles[0].getDicomTagValue(DicomTags.StudyDescription);
    }

    public get seriesDescription(): string {
        if (!this.dicomFiles || this.dicomFiles.length === 0) return "";
        return this.dicomFiles[0].getDicomTagValue(DicomTags.SeriesDescription);
    }
}
